import React, { createContext, useContext, useState, useCallback, ReactNode, useRef } from 'react';
import { driveService } from '../services/api/google.service';
import { useToast } from './ToastContext';
import { useTranslation } from 'react-i18next';

// Types
export interface DragItem {
  id: string;
  name: string;
  type: 'file' | 'folder';
  mimeType?: string;
  parentId?: string;
}

interface DragState {
  isDragging: boolean;
  draggedItems: DragItem[];
  dropTargetId: string | null;
  sourceFolderId: string | null;
}

interface DragDropContextType {
  state: DragState;
  startDrag: (items: DragItem[], sourceFolderId: string) => void;
  endDrag: () => void;
  setDropTarget: (folderId: string | null) => void;
  executeDrop: (targetFolderId: string, onSuccess?: () => void) => Promise<boolean>;
  executeTrashDrop: (onSuccess?: () => void) => Promise<boolean>;
  canDrop: (targetFolderId: string) => boolean;
  // Synchronous check using ref - avoids timing issues with React state
  checkIsDragging: () => boolean;
}

const initialState: DragState = {
  isDragging: false,
  draggedItems: [],
  dropTargetId: null,
  sourceFolderId: null,
};

const DragDropContext = createContext<DragDropContextType | null>(null);

export const useDragDrop = () => {
  const context = useContext(DragDropContext);
  if (!context) {
    throw new Error('useDragDrop must be used within a DragDropProvider');
  }
  return context;
};

interface DragDropProviderProps {
  children: ReactNode;
}

export const DragDropProvider: React.FC<DragDropProviderProps> = ({ children }) => {
  const [state, setState] = useState<DragState>(initialState);
  const { showToast } = useToast();
  const { t } = useTranslation();
  const isMovingRef = useRef(false);
  // Use refs to store drag state for reliable access in callbacks (avoids stale closure issues)
  const isDraggingRef = useRef(false);
  const draggedItemsRef = useRef<DragItem[]>([]);
  const sourceFolderIdRef = useRef<string | null>(null);

  const startDrag = useCallback((items: DragItem[], sourceFolderId: string) => {
    isDraggingRef.current = true;
    draggedItemsRef.current = items;
    sourceFolderIdRef.current = sourceFolderId;
    setState({
      isDragging: true,
      draggedItems: items,
      dropTargetId: null,
      sourceFolderId,
    });
  }, []);

  const endDrag = useCallback(() => {
    isDraggingRef.current = false;
    draggedItemsRef.current = [];
    sourceFolderIdRef.current = null;
    setState(initialState);
  }, []);

  const setDropTarget = useCallback((folderId: string | null) => {
    setState(prev => ({
      ...prev,
      dropTargetId: folderId,
    }));
  }, []);

  const canDrop = useCallback((targetFolderId: string): boolean => {
    // Use refs for reliable access (avoids timing issues with React state)
    const isDragging = isDraggingRef.current;
    const draggedItems = draggedItemsRef.current;
    const sourceFolderId = sourceFolderIdRef.current;

    // Cannot drop if not dragging
    if (!isDragging || draggedItems.length === 0) {
      return false;
    }

    // Cannot drop into the same folder (already there)
    if (sourceFolderId === targetFolderId) {
      return false;
    }

    // Cannot drop a folder into itself
    for (const item of draggedItems) {
      if (item.type === 'folder' && item.id === targetFolderId) {
        return false;
      }
    }

    return true;
  }, []);

  // Synchronous check using ref - avoids timing issues with React state
  const checkIsDragging = useCallback(() => {
    return isDraggingRef.current;
  }, []);

  const executeDrop = useCallback(async (
    targetFolderId: string,
    onSuccess?: () => void
  ): Promise<boolean> => {
    // Use ref for reliable access to current drag items
    const draggedItems = draggedItemsRef.current;
    const sourceFolderId = sourceFolderIdRef.current;

    if (draggedItems.length === 0 || isMovingRef.current) return false;
    if (sourceFolderId === targetFolderId) return false;
    for (const item of draggedItems) {
      if (item.type === 'folder' && item.id === targetFolderId) return false;
    }

    isMovingRef.current = true;

    try {
      // Move all items
      const results = await Promise.allSettled(
        draggedItems.map(item => driveService.moveFile(item.id, targetFolderId))
      );

      const failures = results.filter(r => r.status === 'rejected');
      const successes = results.filter(r => r.status === 'fulfilled');

      if (successes.length > 0) {
        // Success notification
        const count = successes.length;
        const message = count === 1
          ? t('fileManager.movedSingle', '«{{name}}» перемещён', { name: draggedItems[0].name })
          : t('fileManager.movedMultiple', 'Перемещено объектов: {{count}}', { count });

        showToast('success', message, false);

        // Small delay to let Google Drive process, then refresh
        await new Promise(resolve => setTimeout(resolve, 300));

        // Callback to refresh file list
        onSuccess?.();
      }

      if (failures.length > 0 && successes.length === 0) {
        const firstError = (failures[0] as PromiseRejectedResult).reason;
        if (firstError?.response?.status === 403) {
          showToast('error', t('fileManager.noMovePermission', 'Нет прав на перемещение в эту папку'), false);
        } else if (firstError?.response?.status === 400) {
          showToast('error', t('fileManager.cannotMoveToSelf', 'Нельзя переместить папку внутрь себя'), false);
        } else {
          showToast('error', t('fileManager.moveFailed', 'Не удалось переместить'), false);
        }
        return false;
      }

      return true;
    } catch (error: any) {
      showToast('error', t('fileManager.moveFailed', 'Не удалось переместить'), false);
      return false;
    } finally {
      isMovingRef.current = false;
      endDrag();
    }
  }, [endDrag, showToast, t]);

  const executeTrashDrop = useCallback(async (
    onSuccess?: () => void
  ): Promise<boolean> => {
    // Use ref for reliable access to current drag items (avoids stale closure)
    const draggedItems = draggedItemsRef.current;

    if (draggedItems.length === 0 || isMovingRef.current) return false;

    isMovingRef.current = true;

    try {
      // Trash all items one by one for better error handling
      const results = await Promise.allSettled(
        draggedItems.map(item => driveService.moveToTrash(item.id))
      );

      // Check results
      const failures = results.filter(r => r.status === 'rejected');
      const successes = results.filter(r => r.status === 'fulfilled');

      if (successes.length > 0) {
        // Success notification
        const count = successes.length;
        const message = count === 1
          ? t('fileManager.trashedSingle', '«{{name}}» перемещён в корзину', { name: draggedItems[0].name })
          : t('fileManager.trashedMultiple', 'Перемещено в корзину объектов: {{count}}', { count });

        showToast('success', message, false);

        // Small delay to let Google Drive process, then refresh
        await new Promise(resolve => setTimeout(resolve, 300));

        // Callback to refresh file list
        onSuccess?.();
      }

      if (failures.length > 0 && successes.length === 0) {
        // All failed
        const firstError = (failures[0] as PromiseRejectedResult).reason;
        if (firstError?.response?.status === 403) {
          showToast('error', t('fileManager.noTrashPermission', 'Нет прав на удаление'), false);
        } else {
          showToast('error', t('fileManager.trashFailed', 'Не удалось переместить в корзину'), false);
        }
        return false;
      }

      return true;
    } catch (error: any) {
      showToast('error', t('fileManager.trashFailed', 'Не удалось переместить в корзину'), false);
      return false;
    } finally {
      isMovingRef.current = false;
      endDrag();
    }
  }, [endDrag, showToast, t]);

  return (
    <DragDropContext.Provider
      value={{
        state,
        startDrag,
        endDrag,
        setDropTarget,
        executeDrop,
        executeTrashDrop,
        canDrop,
        checkIsDragging,
      }}
    >
      {children}
    </DragDropContext.Provider>
  );
};

export default DragDropContext;
